import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Bell, CircleUserRound, CloudSun, LogOut, Settings } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AI } from '@/assets/icons';
import { Button, Clock, NotificationModal, SettingsModal, StatusDot } from '@/components';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdownMenu/dropdown-menu';
import { useAuth, useCommonFeatures, useERSContext, useMapContext, useTheme } from '@/context';
import { capitalizeWords } from '@/lib/utils';
import { AIChatBot } from '@/pages';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const Header = () => {
  const tzName = dayjs.tz.guess();
  const { t } = useTranslation();
  const { chatBot } = useCommonFeatures();
  const { isOpen, toggle, setOpen } = chatBot;
  const { theme, ThemeMode, toggleTheme } = useTheme();
  const { logout } = useAuth();

  const { selfProfileRef, notifications, setNotifications, incidents } = useERSContext();
  const { focusIncident } = useMapContext();

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const title = t('HEADER.TITLE');

  // Update notifications in the context directly
  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const handleNotificationClick = notification => {
    // Find the incident by ID
    const incident = incidents.find(inc => inc.id === notification.incidentId);

    if (incident) {
      // Mark notification as read
      setNotifications(prev => prev.map(n => (n.id === notification.id ? { ...n, isRead: true } : n)));
      // Focus the incident (expand the card)
      focusIncident(incident);
      setNotificationOpen(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <header className="flex items-center justify-between border-b bg px-7 py-3 shadow-sm">
      {/* Title */}
      <h1 className="text-xl font-semibold">{title}</h1>
      {/* Right side */}
      <div className="flex items-center gap-4">
        <span className="px-3 py-1 text-sm rounded-sm bg-secondary text font-medium flex flex-row items-center gap-1">
          <CloudSun size={14} />
          <Clock timeZone={tzName} />
        </span>

        {/* AI Chatbot */}
        <AIChatBot isOpen={isOpen} toggle={toggle} onClose={() => setOpen(false)}>
          <button
            title="AI Assistant"
            aria-label="AI Assistant"
            className="hover:bg-elevated disabled:border-disabled disabled:text-disabled p-3 rounded"
          >
            <AI />
          </button>
        </AIChatBot>

        <NotificationModal
          open={notificationOpen}
          onOpenChange={setNotificationOpen}
          notifications={notifications}
          onMarkAllAsRead={handleMarkAllAsRead}
          onNotificationClick={handleNotificationClick}
        >
          <Button
            variant="ghost"
            size="ghostIcon"
            className="relative"
            aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
          >
            <div className="relative">
              <Bell absoluteStrokeWidth size={20} />
              {unreadCount > 0 && (
                <StatusDot
                  className="absolute top-0 right-0 rounded-full bg-primary"
                  count={unreadCount > 99 ? '99+' : unreadCount}
                />
              )}
            </div>
          </Button>
        </NotificationModal>

        {/* <Button variant="ghost" size="ghostIcon" disabled>
          <TriangleAlert absoluteStrokeWidth size={20} />
        </Button> */}

        {/* User dropdown */}
        <div className="w-8 h-8 flex items-center justify-center rounded-full">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <CircleUserRound absoluteStrokeWidth />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>
                {capitalizeWords(t('HEADER.USERNAME'))}
                <div className="text-muted-foreground text-sm font-normal">{selfProfileRef.current?.displayName}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2"
                onClick={() => {
                  toggleTheme();
                }}
              >
                {ThemeMode.getIcon(theme === ThemeMode.LIGHT ? ThemeMode.DARK : ThemeMode.LIGHT)}
                {theme === ThemeMode.LIGHT ? t('THEME.DARK') : t('THEME.LIGHT')} {t('HEADER.MODE')}
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onClick={() => setSettingsOpen(true)}>
                <Settings />
                {capitalizeWords(t('HEADER.SETTINGS'))}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2" onClick={logout}>
                <LogOut />
                {capitalizeWords(t('HEADER.LOG_OUT'))}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </header>
  );
};
